import axios from "axios";
import { API_URL } from "../configs";
import { TOKEN_KEY } from "../authProvider";

interface ArchetypeValues {
  archetype_values: Record<string, number>;
}

export interface ExpressionWithMatch {
  expression: string;
  match_percentage: number;
}

const instance = axios.create({
  baseURL: API_URL,
});

export const setAuthToken = (token?: string) => {
  if (token) {
    instance.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  } else {
    const _token = window.localStorage.getItem(TOKEN_KEY);
    instance.defaults.headers.common["Authorization"] = `Bearer ${_token}`;
  }
};

export const getExpressions = async (
  data: ArchetypeValues
): Promise<ExpressionWithMatch[]> => {
  const response = await instance.post<ExpressionWithMatch[]>(
    "/expressions",
    data
  );
  return response.data;
};

export interface LaraResource {
  id: string;
  name: string;
}

export interface LaraResourcesResponse {
  glossaries: LaraResource[];
  memories: LaraResource[];
  error?: string;
}

export const getLaraResources = async (): Promise<LaraResourcesResponse> => {
  const response = await instance.get<{ data: LaraResourcesResponse }>(
    "/lara/resources"
  );
  return response.data.data;
};

export interface TranslateDocumentParams {
  file: File;
  source?: string;
  target: string;
  glossaries?: string[];
  memories?: string[];
  style?: "faithful" | "fluid" | "creative";
}

export const translateDocument = async (
  params: TranslateDocumentParams
): Promise<Blob> => {
  const formData = new FormData();
  formData.append("file", params.file);
  if (params.source) {
    formData.append("source", params.source);
  }
  formData.append("target", params.target);

  if (params.glossaries && params.glossaries.length > 0) {
    formData.append("glossaries", JSON.stringify(params.glossaries));
  }
  if (params.memories && params.memories.length > 0) {
    formData.append("memories", JSON.stringify(params.memories));
  }
  if (params.style) {
    formData.append("style", params.style);
  }

  const response = await instance.post("/lara/translate-document", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
    responseType: "blob",
  });

  // Server returns JSON for errors even with blob responseType
  if (response.headers["content-type"]?.includes("application/json")) {
    const text = await response.data.text();
    const error = JSON.parse(text);
    throw new Error(error.detail || error.message || "Translation failed");
  }

  return response.data;
};

export const previewDocx = async (file: File): Promise<Blob> => {
  const formData = new FormData();
  formData.append("file", file);

  const response = await instance.post("/lara/preview-docx", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
    responseType: "blob",
  });

  // Check if response is actually a PDF (not an error response)
  const contentType = response.headers["content-type"];
  if (contentType?.includes("application/json")) {
    const text = await response.data.text();
    const error = JSON.parse(text);
    throw new Error(error.detail || error.message || "Preview conversion failed");
  }

  return response.data;
};

export default instance;
